import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import LightbulbIcon from "@mui/icons-material/LightbulbRounded";
import RecommendIcon from "@mui/icons-material/HealthAndSafetyRounded";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import TrendingFlatRounded from "@mui/icons-material/TrendingFlatRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getInsights } from "../../services/timeline.service";
import "./style.scss";

/* ─── Status → visual config ──────────────────────────────────────── */
const STATUS_CFG = {
  positive: { Icon: CheckCircleRounded, cls: "insights-card-v2--positive" },
  warning:  { Icon: WarningAmberRounded, cls: "insights-card-v2--warning" },
  critical: { Icon: ErrorOutlineRounded, cls: "insights-card-v2--critical" },
  stable:   { Icon: TrendingFlatRounded, cls: "insights-card-v2--stable" }
};

function InsightCardV2({ card, idx }) {
  const cfg = STATUS_CFG[card.status] || STATUS_CFG.stable;
  const { Icon } = cfg;
  const points = card.data_points || [];

  return (
    <motion.article
      className={`insights-card-v2 ${cfg.cls}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
    >
      <div className="insights-card-v2__top">
        <div className="insights-card-v2__icon">
          <Icon sx={{ fontSize: 18 }} />
        </div>
        <span className="insights-card-v2__status">{card.status}</span>
      </div>

      <h4 className="insights-card-v2__title">{card.title}</h4>
      <p className="insights-card-v2__desc">{card.description}</p>

      {points.length > 0 && (
        <div className="insights-card-v2__pills">
          {points.map((dp, i) => (
            <span key={i} className="insights-card-v2__pill">{dp}</span>
          ))}
        </div>
      )}

      {card.action && (
        <p className="insights-card-v2__action">→ {card.action}</p>
      )}
    </motion.article>
  );
}

export default function Insights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      setLoading(true);
      const data = await getInsights();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load insights.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Extract structured cards from derived bundles
  const allCards = useMemo(() => {
    const cards = [];
    for (const item of items) {
      if (item.cards && Array.isArray(item.cards)) {
        cards.push(...item.cards);
      }
    }
    return cards;
  }, [items]);

  // Legacy items that have insights/recommendations but no cards
  const legacyItems = useMemo(
    () =>
      items.filter(
        (item) =>
          (!item.cards || item.cards.length === 0) &&
          ((item.insights || []).length > 0 || (item.recommendations || []).length > 0)
      ),
    [items]
  );

  const hasContent = allCards.length > 0 || legacyItems.length > 0;

  return (
    <div data-tour="insights-panel" className="insights-page">
      <header className="insights-page__header">
        <div className="insights-page__title-block">
          <div className="insights-page__icon-wrap">
            <AutoAwesomeIcon />
          </div>
          <div>
            <h1>AI insights</h1>
            <p>
              Summaries from your stored reports (rule-based today) plus any full AI runs saved to your account. Always
              confirm with a clinician before changing care.
            </p>
          </div>
        </div>
        <div className="insights-page__actions">
          <Link to="/upload" className="insights-page__btn insights-page__btn--primary">
            Upload report
          </Link>
          <button type="button" className="insights-page__btn insights-page__btn--ghost" onClick={load} disabled={loading}>
            <RefreshIcon sx={{ fontSize: 20 }} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      {error ? <p className="insights-page__error">{error}</p> : null}

      {loading ? (
        <div className="insights-page__skeleton" aria-hidden>
          {[1, 2].map((k) => (
            <div key={k} className="insights-page__skeleton-card" />
          ))}
        </div>
      ) : !hasContent ? (
        <motion.div
          className="insights-page__empty"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <AutoAwesomeIcon className="insights-page__empty-icon" />
          <h2>No insights yet</h2>
          <p>Upload a PDF lab report so we can derive summaries and show them here alongside your timeline.</p>
          <Link to="/upload" className="insights-page__btn insights-page__btn--primary">
            Upload a report
          </Link>
        </motion.div>
      ) : (
        <>
          {/* Structured insight cards (new format) */}
          {allCards.length > 0 && (
            <div className="insights-page__cards-grid">
              {allCards.map((card, idx) => (
                <InsightCardV2 key={`card-${idx}`} card={card} idx={idx} />
              ))}
            </div>
          )}

          {/* Legacy flat insight bundles */}
          {legacyItems.length > 0 && (
            <div className="insights-page__grid">
              {legacyItems.map((item, idx) => {
                const insights = item.insights || [];
                const recs = item.recommendations || [];
                const dateSrc = item.createdAt || item.updatedAt;
                const dateLabel = dateSrc
                  ? new Date(dateSrc).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short"
                    })
                  : "Recent";

                return (
                  <motion.article
                    key={item._id || idx}
                    className={`insights-card ${item.isDerived ? "insights-card--derived" : ""}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06 }}
                  >
                    <div className="insights-card__top">
                      <div className="insights-card__meta">
                        {item.isDerived ? (
                          <span className="insights-card__badge">From your latest labs</span>
                        ) : (
                          <span className="insights-card__badge insights-card__badge--muted">Saved insight</span>
                        )}
                        <time className="insights-card__time" dateTime={dateSrc}>
                          {dateLabel}
                        </time>
                      </div>
                    </div>

                    {insights.length > 0 && (
                      <section className="insights-card__section">
                        <h3>
                          <LightbulbIcon sx={{ fontSize: 22 }} />
                          Insights
                        </h3>
                        <ul>
                          {insights.map((line, i) => (
                            <li key={`${item._id}-in-${i}`}>{line}</li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {recs.length > 0 && (
                      <section className="insights-card__section insights-card__section--rec">
                        <h3>
                          <RecommendIcon sx={{ fontSize: 22 }} />
                          Recommendations
                        </h3>
                        <ul>
                          {recs.map((line, i) => (
                            <li key={`${item._id}-rec-${i}`}>{line}</li>
                          ))}
                        </ul>
                      </section>
                    )}
                  </motion.article>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
